import {
  useGetFieldDefinitions,
  useGetSyncStatus,
  useGetProductDetail,
  useGetOptionMappings,
  useSaveProductOverride,
  useDeleteProductOverride,
  useToggleVariantExclude,
  useSaveVariantOverrides,
  useGetUploadUrl,
  getGetProductDetailQueryKey,
  getGetProductsQueryKey,
} from '@/api/generated/v2-catalog/v2-catalog'
import type {
  Cafe24ProductResponse as Cafe24ProductResponse,
  Cafe24ProductVariant,
  Cafe24ProductResponseAdditionalimagesItem,
  CatalogSyncStatusResponse,
  CatalogVariantOverrideRequest,
  CatalogProductOverrideRequest,
  CategoryFieldDefinitionsResponse as CategoryFieldDefinitionsResponse,
  CapacityUnit,
  FieldDefinition,
  FieldOption,
  FileUploadResponse,
  OptionFieldMapping,
  OptionInfo,
  OptionMappingsResponse as OptionMappingsResponse,
} from '@/api/schemas'
import { FbCategorySelector } from '@/features/catalog/FbCategorySelector'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Dialog, DialogContent } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Select, SelectOption } from '@/components/ui/Select'
import { auth } from '@/lib/auth'
import { useShop } from '@/lib/shopContext'
import {
  UPLOAD_LIMITS,
  UPLOAD_LIMITS_HINT,
  UploadError,
  prepareUploadBatch,
  toastUploadRejections,
  uploadErrorMessage,
  validateUploadFile,
} from '@/lib/uploadLimits'
import type { CapacityValue } from '@/types'
import { useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import axios from 'axios'
import { ArrowLeft, ChevronDown, ChevronRight, Loader2, Plus, Save, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useConfirm } from '@/hooks/useConfirm'

// Meta 필드 한국어 레이블 매핑
const META_FIELD_LABELS: Record<string, string> = {
  color: '색상',
  size: '크기',
  material: '소재',
  pattern: '패턴',
}

// Meta 카탈로그가 상품당 받는 미디어 개수 — transformers.py 의
// additional_image_link[:10] / video[:20] 과 같은 값이다 (넘기면 동기화 때 조용히 잘린다)
const CATALOG_MEDIA_MAX_COUNT = { image: 10, video: 20 } as const

// 카탈로그 상품명 최대 길이 — 백엔드 CUSTOM_TITLE_MAX_LENGTH·DB String(150)·Meta title 상한과 같다
const CUSTOM_TITLE_MAX_LENGTH = 150

// 한국어 조사 "이/가" 처리 (받침 유무에 따라 선택)
function getSubjectParticle(text: string): string {
  if (!text) return '이'
  const lastChar = text[text.length - 1]
  const code = lastChar.charCodeAt(0)
  // 한글 범위 (가 ~ 힣): 0xAC00 ~ 0xD7A3
  if (code >= 0xac00 && code <= 0xd7a3) {
    // 받침 유무: (code - 0xAC00) % 28 === 0 이면 받침 없음
    return (code - 0xac00) % 28 === 0 ? '가' : '이'
  }
  // 숫자로 끝나면 "이" (예: "123이")
  if (/[0-9]$/.test(text)) return '이'
  // 영어로 끝나면 발음 기준 (대부분 "이" 사용)
  return '이'
}

// fb_product_category ID → 텍스트 경로 조회
interface FlatCategory {
  id: string
  path: string
}

// 카테고리 데이터를 lazy load (829KB → 필요할 때만 로드)
let cachedFlatList: FlatCategory[] | null = null
async function loadFlatList(): Promise<FlatCategory[]> {
  if (cachedFlatList) return cachedFlatList
  const mod = await import('@/data/fbProductCategories.json')
  cachedFlatList = mod.default.flatList as FlatCategory[]
  return cachedFlatList
}

function getCategoryPath(categoryId: string | null): string | null {
  if (!categoryId || !cachedFlatList) return null
  const category = cachedFlatList.find((c) => c.id === categoryId)
  return category?.path || null
}

/**
 * FB 카테고리명에서 gender/age_group 자동 추출
 * ⚠️ 주의: 이 패턴 로직은 backend/src/utils/category_parser.py에도 동일하게 구현되어 있음.
 *    패턴 수정 시 양쪽 모두 업데이트 필요!
 */
interface InferredFields {
  gender: string | null
  age_group: string | null
}

const GENDER_PATTERNS: [RegExp, string][] = [
  [/여성용|여성|여자|우먼|레이디/i, 'female'],
  [/남성용|남성|남자|맨즈/i, 'male'],
  [/남녀공용|유니섹스|공용/i, 'unisex'],
]

const AGE_GROUP_PATTERNS: [RegExp, string][] = [
  [/신생아/i, 'newborn'],
  [/영아/i, 'infant'],
  [/유아용품|유아용|유아/i, 'toddler'],
  [/아동용|아동|키즈|어린이/i, 'kids'],
  [/청소년|틴|주니어/i, 'teen'],
  [/성인용|성인|어덜트/i, 'adult'],
]

function extractGenderAgeFromFbCategory(
  categoryPath: string | null,
): InferredFields {
  const result: InferredFields = { gender: null, age_group: null }
  if (!categoryPath) return result

  for (const [pattern, value] of GENDER_PATTERNS) {
    if (pattern.test(categoryPath)) {
      result.gender = value
      break
    }
  }

  for (const [pattern, value] of AGE_GROUP_PATTERNS) {
    if (pattern.test(categoryPath)) {
      result.age_group = value
      break
    }
  }

  return result
}

// URL 검색 파라미터 타입 (목록 페이지 상태 유지용)
interface ProductEditSearchParams {
  q?: string // 검색어
  page?: number // 페이지 번호
}

export const Route = createFileRoute('/_dashboard/catalog/$productNo')({
  component: CatalogProductEditPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): ProductEditSearchParams => {
    return {
      q: typeof search.q === 'string' ? search.q : undefined,
      page: typeof search.page === 'number' ? search.page : undefined,
    }
  },
})

function CatalogProductEditPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirm()
  const { q: listSearch, page: listPage } = Route.useSearch()

  // 목록 페이지로 돌아가는 함수 (검색/페이지 상태 유지)
  const navigateToList = useCallback(() => {
    navigate({
      to: '/catalog',
      search: { q: listSearch || undefined, page: listPage || undefined },
    })
  }, [navigate, listSearch, listPage])
  const { productNo } = Route.useParams()
  // shop 은 레이아웃이 useGetShop 으로 채운 컨텍스트에서 읽는다 (여기서 재조회하지 않는다)
  const { shop } = useShop()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // 탭 상태: 상품 설정 / 옵션별 설정
  const [activeTab, setActiveTab] = useState<'product' | 'variants'>('product')
  // 카페24 원본 정보 펼침 상태
  const [showOriginalInfo, setShowOriginalInfo] = useState(false)
  // 미디어 미리보기 모달
  const [previewMedia, setPreviewMedia] = useState<{ type: 'image' | 'video'; url: string } | null>(null)
  // 카테고리 선택 모달
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  // 모달 내 임시 선택값 (선택 완료 전까지는 draft에만 반영해서, id를 가진 상위 카테고리를 골라도 더 깊은 하위로 계속 탐색 가능)
  const [draftCategoryId, setDraftCategoryId] = useState<string | null>(null)

  // Variant 관련 상태
  const [variantsExpanded, setVariantsExpanded] = useState(false)
  const [expandedVariantCode, setExpandedVariantCode] = useState<string | null>(null)
  const [variantOverrides, setVariantOverrides] = useState<
    Record<string, CatalogVariantOverrideRequest>
  >({})
  const [togglingVariant, setTogglingVariant] = useState<string | null>(null)

  // 편집 폼 상태
  // 카탈로그 노출 제품명 (REQ-174). 빈 칸이면 카페24 상품명이 그대로 나간다
  const [customTitle, setCustomTitle] = useState('')
  // 공백만 든 입력은 "비우기"로 받지 않고 에러로 막는다 (백엔드도 422). 완전히 빈 칸은 복귀라 정상
  const customTitleError =
    customTitle !== '' && customTitle.trim() === ''
      ? '공백만 입력할 수 없어요. 카페24 상품명으로 되돌리려면 칸을 비워 주세요.'
      : undefined
  const [customDescription, setCustomDescription] = useState('')
  const [customVideoUrls, setCustomVideoUrls] = useState<string[]>([]) // 동영상 배열 (최대 20개)
  const [customImageUrls, setCustomImageUrls] = useState<string[]>([])
  const [fbProductCategoryId, setFbProductCategoryId] = useState<string | null>(
    null,
  )
  const [customBrand, setCustomBrand] = useState('')
  const [customMainImageUrl, setCustomMainImageUrl] = useState('')
  // 카테고리 세부 필드 값 (string 또는 capacity 객체)
  const [categorySpecificFields, setCategorySpecificFields] = useState<
    Record<string, string | CapacityValue>
  >({})

  // 옵션 필드 매핑 상태 (로컬 편집용)
  const [optionMappings, setOptionMappings] =
    useState<OptionMappingsResponse | null>(null)

  // ==================== TanStack Query 훅 ====================

  // 카테고리별 세부 필드 정의
  const { data: fieldDefsData } = useGetFieldDefinitions({
    query: { enabled: !!shop },
  })
  // custom-instance가 !response.ok 시 throw하므로, 성공 응답만 반환된다
  const fieldDefs = (fieldDefsData ?? null) as CategoryFieldDefinitionsResponse | null

  // 동기화 상태 (동기화 중이면 5초 폴링)
  const { data: syncStatusData } = useGetSyncStatus(
    { limit: 1 },
    {
      query: {
        enabled: !!shop,
        refetchInterval: (query) => {
          const currentStatus = (query.state.data as CatalogSyncStatusResponse | undefined)?.config?.meta_catalog_sync_status
          return currentStatus === 'running' ? 5000 : false
        },
      },
    },
  )
  const syncStatus = (syncStatusData ?? null) as CatalogSyncStatusResponse | null
  const isSyncRunning =
    syncStatus?.config.meta_catalog_sync_status === 'running'

  // 상품 상세 조회
  const { data: productDetailData, isLoading: loadingProduct } = useGetProductDetail(
    Number(productNo),
    {
      query: {
        enabled: !!shop && !!productNo,
      },
    },
  )
  const product = (productDetailData ?? null) as Cafe24ProductResponse | null

  // 옵션 매핑 조회
  const { data: optionMappingsData, isLoading: loadingOptionMappings } = useGetOptionMappings(
    Number(productNo),
    {
      query: {
        enabled: !!shop && !!productNo && (product?.variants?.length ?? 0) > 0,
      },
    },
  )

  // Mutation 훅들
  const saveProductOverrideMutation = useSaveProductOverride()
  const deleteProductOverrideMutation = useDeleteProductOverride()
  const toggleVariantExcludeMutation = useToggleVariantExclude()
  const saveVariantOverridesMutation = useSaveVariantOverrides()
  const getUploadUrlMutation = useGetUploadUrl()

  // 업로드 진행 중 여부 — 큰 동영상은 수십 초가 걸려서, 표시가 없으면 멈춘 것처럼 보인다.
  // 진행 중에는 파일 선택도 잠가서 배치가 겹쳐 상한을 넘기는 것까지 함께 막는다
  const [uploadingCount, setUploadingCount] = useState(0)
  const isUploading = uploadingCount > 0

  // 파일 업로드 헬퍼: Signed URL 발급 + GCS에 직접 PUT
  // 실패 시 던지는 Error 의 message 는 그대로 사용자에게 노출되므로 항상 한국어 사유를 담는다
  const uploadFile = useCallback(
    async (file: File, fileType: 'image' | 'video', productNoForUpload: number) => {
      // 서버에 보내기 전에 형식·용량을 먼저 확인 (불필요한 왕복 없이 사유를 바로 안내)
      const invalidReason = validateUploadFile(file, fileType)
      if (invalidReason) throw new UploadError(invalidReason)

      setUploadingCount((prev) => prev + 1)
      try {
        // custom-instance 가 실패 응답을 { status, code, message } 로 throw 하므로 그대로 전파한다
        const response = await getUploadUrlMutation.mutateAsync({
          data: {
            file_type: fileType,
            content_type: file.type,
            original_filename: file.name,
            file_size: file.size,
            product_no: productNoForUpload,
          },
        })
        // custom-instance가 에러를 throw하므로, 성공 응답의 data만 반환됨
        const uploadData = response as unknown as FileUploadResponse
        // GCS Signed URL에 직접 PUT (Orval 대상 아님)
        // axios 에러 메시지는 영문이라 그대로 노출하지 않고 한국어 사유로 감싼다
        try {
          await axios.put(uploadData.upload_url, file, {
            headers: { 'Content-Type': file.type },
          })
        } catch (error) {
          // 원인(서명 URL 만료, CORS 등)은 CS 재현에 필요하므로 콘솔에는 남긴다
          console.error('Upload PUT failed:', error)
          throw new UploadError('파일 전송에 실패했어요. 잠시 후 다시 시도해주세요.')
        }
        return uploadData
      } finally {
        setUploadingCount((prev) => prev - 1)
      }
    },
    [getUploadUrlMutation],
  )

  // 카테고리 path에서 카테고리 그룹 추출
  const getCategoryGroup = useCallback(
    (categoryPath: string | null): string => {
      if (!categoryPath || !fieldDefs) return 'default'
      const topCategory = categoryPath.split(' > ')[0].trim().toLowerCase()
      return fieldDefs.category_group_mapping[topCategory] || 'default'
    },
    [fieldDefs],
  )

  // 옵션 매핑에서 사용 중인 Meta 필드 목록 (충돌 방지용)
  const mappedMetaFields = useMemo(() => {
    if (!optionMappings) return new Set<string>()
    const fields = new Set<string>()
    for (const opt of optionMappings.options) {
      if (opt.mapping?.field) {
        fields.add(opt.mapping.field)
      }
    }
    return fields
  }, [optionMappings])

  // 선택된 카테고리에 따른 표시할 필드 목록 (서브카테고리 추가 필드 포함)
  const applicableFields = useMemo(() => {
    if (!fieldDefs) return []
    const categoryPath = getCategoryPath(fbProductCategoryId)
    const categoryGroup = getCategoryGroup(categoryPath)

    // 기본 필드 목록
    const baseFields =
      fieldDefs.category_fields[categoryGroup] ||
      fieldDefs.category_fields.default ||
      []

    // 서브카테고리별 추가 필드 확인 (예: "자외선 차단제" 포함 시 spf_value 추가)
    const subcategoryFields: string[] = []
    if (fieldDefs.subcategory_fields && categoryPath) {
      const pathLower = categoryPath.toLowerCase()
      for (const [keyword, fields] of Object.entries(
        fieldDefs.subcategory_fields,
      )) {
        if (pathLower.includes(keyword.toLowerCase())) {
          subcategoryFields.push(...(fields as string[]))
        }
      }
    }

    // 중복 제거 후 반환
    return [...new Set([...baseFields, ...subcategoryFields])]
  }, [fbProductCategoryId, fieldDefs, getCategoryGroup])

  // 선택된 카테고리에서 자동 추출된 gender/age_group (카테고리 변경 시 즉시 업데이트)
  const inferredFields = useMemo(() => {
    const categoryPath = getCategoryPath(fbProductCategoryId)
    return extractGenderAgeFromFbCategory(categoryPath)
  }, [fbProductCategoryId])

  // ==================== 초기화 & 부수 효과 ====================

  // 인증 확인 + 카테고리 데이터 로드 (shop 은 레이아웃이 채운 컨텍스트에서 읽는다)
  useEffect(() => {
    const loadInitialData = async () => {
      if (!auth.isAuthenticated()) {
        navigate({ to: '/' })
        return
      }
      try {
        await loadFlatList() // fbProductCategories.json lazy load
      } catch (error) {
        console.error('Failed to load initial data:', error)
        navigate({ to: '/' })
      } finally {
        setLoading(false)
      }
    }
    loadInitialData()
  }, [navigate])

  // 상품 데이터가 로드되면 폼 초기값 설정
  useEffect(() => {
    if (!product) return

    if (product.override) {
      // override 있으면 해당 값 사용
      setCustomTitle(product.override.custom_title || '')
      setCustomDescription(product.override.custom_description || '')
      setCustomVideoUrls(product.override.custom_video_urls || [])
      setCustomImageUrls(product.override.custom_image_urls || [])
      // 카테고리: override에 저장된 값 우선, 없으면 매핑된 값 자동 선택
      setFbProductCategoryId(
        product.override.fb_product_category_id ||
          product.mapped_fb_product_category_id ||
          null,
      )
      setCustomBrand(product.override.custom_brand || '')
      setCustomMainImageUrl(product.override.custom_main_image_url || '')
      setCategorySpecificFields(
        (product.override.category_specific_fields as Record<string, string | CapacityValue>) || {},
      )
    } else {
      // override 없으면 매핑된 카테고리 자동 선택
      setCustomTitle('')
      setCustomDescription('')
      setCustomVideoUrls([])
      setCustomImageUrls([])
      setFbProductCategoryId(
        product.mapped_fb_product_category_id || null,
      )
      setCustomBrand('')
      setCustomMainImageUrl('')
      setCategorySpecificFields({})
    }

    // Variant overrides 초기화
    if ((product.variant_overrides?.length ?? 0) > 0) {
      const voMap: Record<string, CatalogVariantOverrideRequest> = {}
      for (const vo of product.variant_overrides ?? []) {
        voMap[vo.variant_code] = {
          variant_code: vo.variant_code,
          exclude_from_catalog: vo.exclude_from_catalog,
          custom_title: vo.custom_title,
          custom_description: vo.custom_description,
          custom_main_image_url: vo.custom_main_image_url,
          custom_image_urls: vo.custom_image_urls,
          custom_video_urls: vo.custom_video_urls,
        }
      }
      setVariantOverrides(voMap)
    } else {
      setVariantOverrides({})
    }
  }, [product])

  // 옵션 매핑 데이터가 로드되면 로컬 상태에 반영
  useEffect(() => {
    const mappingsPayload = (optionMappingsData ?? undefined) as OptionMappingsResponse | undefined
    if (mappingsPayload) {
      setOptionMappings(mappingsPayload)
    }
  }, [optionMappingsData])

  // 옵션 매핑 변경 핸들러
  const handleOptionMappingChange = (
    optionName: string,
    field: string | null,
    combineOrder?: number | null,
  ) => {
    if (!optionMappings) return

    // field가 새로 선택되면 다음 빈 순번 자동 할당
    let finalCombineOrder = combineOrder
    if (field && combineOrder === undefined) {
      // 현재 옵션의 기존 field 확인
      const currentOpt = optionMappings.options.find(
        (opt: OptionInfo) => opt.name === optionName,
      )
      const currentField = currentOpt?.mapping?.field

      // field가 변경되었거나 새로 선택된 경우
      if (currentField !== field) {
        finalCombineOrder = getNextAvailableCombineOrder(field, optionName)
      } else {
        finalCombineOrder = currentOpt?.mapping?.combine_order || 1
      }
    }

    const updatedOptions = optionMappings.options.map((opt: OptionInfo) => {
      if (opt.name === optionName) {
        return {
          ...opt,
          mapping: field
            ? { field, combine_order: finalCombineOrder || null }
            : null,
          is_auto_detected: false, // 사용자가 변경하면 자동 인식 아님
        }
      }
      return opt
    })

    setOptionMappings({
      ...optionMappings,
      options: updatedOptions,
    })
  }

  // 같은 필드에 여러 옵션이 매핑되어 있는지 확인
  const getFieldMappingCount = (field: string): number => {
    if (!optionMappings) return 0
    return optionMappings.options.filter((opt: OptionInfo) => opt.mapping?.field === field)
      .length
  }

  // 특정 필드에서 사용 중인 combine_order 목록 반환 (특정 옵션 제외)
  const getUsedCombineOrders = (
    field: string,
    excludeOptionName?: string,
  ): number[] => {
    if (!optionMappings) return []
    return optionMappings.options
      .filter(
        (opt: OptionInfo) => opt.mapping?.field === field && opt.name !== excludeOptionName,
      )
      .map((opt: OptionInfo) => opt.mapping?.combine_order || 1)
  }

  // 특정 필드에서 다음 사용 가능한 combine_order 반환
  const getNextAvailableCombineOrder = (
    field: string,
    excludeOptionName?: string,
  ): number => {
    const usedOrders = getUsedCombineOrders(field, excludeOptionName)
    for (let i = 1; i <= 4; i++) {
      if (!usedOrders.includes(i)) return i
    }
    return 1 // 모두 사용 중이면 1 반환 (4개 초과는 드물음)
  }

  const handleSave = async () => {
    if (!product) return
    // 공백만 든 제품명은 실수일 수 있어 저장하지 않는다 (되돌리기는 칸을 완전히 비우는 것으로만)
    if (customTitleError) {
      toast.error(customTitleError)
      return
    }

    setSaving(true)
    try {
      // 카테고리별 세부 필드: 빈 값 제거, capacity 객체는 그대로 전달
      const filteredSpecificFields: Record<string, string | CapacityValue> = {}
      for (const [key, value] of Object.entries(categorySpecificFields)) {
        if (typeof value === 'string') {
          // 문자열: 공백 제거 후 빈 값이 아니면 저장
          if (value.trim()) {
            filteredSpecificFields[key] = value.trim()
          }
        } else if (typeof value === 'object' && value !== null) {
          // capacity 객체: value가 있으면 저장
          const capValue = value as CapacityValue
          if (capValue.value && capValue.value.trim()) {
            filteredSpecificFields[key] = {
              value: capValue.value.trim(),
              unit: capValue.unit || 'ml',
            }
          }
        }
      }

      // 옵션 매핑 데이터 구성
      let optionFieldMappings: Record<string, OptionFieldMapping> | null = null
      if (optionMappings && optionMappings.options.length > 0) {
        const mappings: Record<string, OptionFieldMapping> = {}
        for (const opt of optionMappings.options) {
          if (opt.mapping && opt.mapping.field) {
            mappings[opt.name] = opt.mapping
          }
        }
        if (Object.keys(mappings).length > 0) {
          optionFieldMappings = mappings
        }
      }

      const data: CatalogProductOverrideRequest = {
        // 빈 칸 → null (카페24 원래 이름으로 복귀). 앞뒤 공백은 잘라서 보낸다
        custom_title: customTitle.trim() || null,
        custom_description: customDescription?.trim() || null,
        custom_video_urls: customVideoUrls.length > 0 ? customVideoUrls : null,
        custom_image_urls: customImageUrls.length > 0 ? customImageUrls : null,
        fb_product_category_id: fbProductCategoryId || null,
        custom_brand: customBrand || null,
        custom_main_image_url: customMainImageUrl || null,
        category_specific_fields:
          Object.keys(filteredSpecificFields).length > 0
            ? filteredSpecificFields
            : null,
        option_field_mappings: optionFieldMappings,
      }

      await saveProductOverrideMutation.mutateAsync({
        productNo: product.product_no,
        data,
      })
      // 상세 + 목록 캐시 무효화 (목록으로 돌아갔을 때 최신 상태 반영)
      queryClient.invalidateQueries({ queryKey: getGetProductDetailQueryKey(product.product_no) })
      queryClient.invalidateQueries({ queryKey: getGetProductsQueryKey() })
      toast.success('상품 설정이 저장되었어요. 동기화하면 카탈로그에 반영돼요.')
      navigateToList() // 목록으로 이동 (검색/페이지 상태 유지)
    } catch (error: unknown) {
      console.error('Failed to save override:', error)
      // 서버 에러 메시지 추출
      let errorMessage = '저장에 실패했어요.'
      if (error && typeof error === 'object' && 'detail' in error) {
        errorMessage = (error as { detail: string }).detail
      }
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    if (!product) return

    const ok = await confirm('커스텀 설정을 삭제하고 카페24 원본으로 복원하시겠습니까?', { variant: 'danger' })
    if (!ok) return

    setSaving(true)
    try {
      await deleteProductOverrideMutation.mutateAsync({
        productNo: product.product_no,
      })
      toast.success('카페24 원본으로 복원되었어요.')
      // 상품 상세 + 목록 캐시 무효화 (복원 후 목록에도 반영)
      queryClient.invalidateQueries({ queryKey: getGetProductDetailQueryKey(product.product_no) })
      queryClient.invalidateQueries({ queryKey: getGetProductsQueryKey() })
    } catch (error) {
      console.error('Failed to delete override:', error)
      toast.error('복원에 실패했어요.')
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveImageUrl = (index: number) => {
    setCustomImageUrls(customImageUrls.filter((_, i) => i !== index))
  }

  // Variant 가격 계산
  const getVariantPrice = (additionalAmount: number) => {
    if (!product) return 0
    return product.price + additionalAmount
  }

  // Variant 가격 범위 (min ~ max)
  const priceRange = useMemo(() => {
    const variants = product?.variants ?? []
    if (!product || variants.length === 0) return null
    const prices = variants.map((v: Cafe24ProductVariant) => product.price + (v.additional_amount ?? 0))
    const min = Math.min(...prices)
    const max = Math.max(...prices)
    return { min, max, isSame: min === max }
  }, [product])

  // Variant exclude 토글
  const handleVariantExclude = async (variantCode: string, exclude: boolean) => {
    if (!product) return
    setTogglingVariant(variantCode)
    try {
      await toggleVariantExcludeMutation.mutateAsync({
        productNo: product.product_no,
        variantCode,
        data: { exclude },
      })
      // 상세 캐시 무효화 (variant 상태 변경 반영)
      queryClient.invalidateQueries({ queryKey: getGetProductDetailQueryKey(product.product_no) })
      setVariantOverrides((prev) => ({
        ...prev,
        [variantCode]: {
          ...prev[variantCode],
          variant_code: variantCode,
          exclude_from_catalog: exclude,
        },
      }))
    } catch {
      toast.error('설정 변경에 실패했어요.')
    } finally {
      setTogglingVariant(null)
    }
  }

  // 개별 variant 저장
  const [savingVariant, setSavingVariant] = useState<string | null>(null)
  const handleSaveVariant = async (variantCode: string) => {
    if (!product) return
    const vo = variantOverrides[variantCode]
    if (!vo) return

    setSavingVariant(variantCode)
    try {
      await saveVariantOverridesMutation.mutateAsync({
        productNo: product.product_no,
        data: { overrides: [vo] },
      })
      // 상세 캐시 무효화 (variant override 변경 반영)
      queryClient.invalidateQueries({ queryKey: getGetProductDetailQueryKey(product.product_no) })
      toast.success('variant 설정이 저장되었어요.')
    } catch {
      toast.error('저장에 실패했어요.')
    } finally {
      setSavingVariant(null)
    }
  }

  // Variant override 필드 업데이트
  const updateVariantOverride = (
    variantCode: string,
    field: keyof CatalogVariantOverrideRequest,
    value: unknown,
  ) => {
    setVariantOverrides((prev) => ({
      ...prev,
      [variantCode]: {
        ...prev[variantCode],
        variant_code: variantCode,
        [field]: value,
      },
    }))
  }

  // Variant override 의 URL 배열 필드에 항목 추가
  // 여러 파일을 연속 업로드할 때 렌더 시점의 variantOverrides 를 읽으면 직전 항목이
  // 유실되므로, 항상 최신 상태(prev)를 기준으로 append 한다
  const appendVariantOverrideUrl = (
    variantCode: string,
    field: 'custom_image_urls' | 'custom_video_urls',
    url: string,
    maxCount: number,
  ) => {
    setVariantOverrides((prev) => {
      const current = prev[variantCode]?.[field] || []
      // 업로드 중 다른 배치가 겹쳐 상한을 넘기면 Meta 동기화 때 초과분이 조용히 잘리므로 여기서 막는다
      if (current.length >= maxCount) return prev
      return {
        ...prev,
        [variantCode]: {
          ...prev[variantCode],
          variant_code: variantCode,
          [field]: [...current, url],
        },
      }
    })
  }

  const handleRemoveVideoUrl = (index: number) => {
    setCustomVideoUrls(customVideoUrls.filter((_, i) => i !== index))
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price) + '원'
  }

  // HTML 태그 제거 (style, script 태그 내용 포함)
  const stripHtml = (html: string) => {
    return html
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // style 태그 전체 제거
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // script 태그 전체 제거
      .replace(/<[^>]+>/g, '') // 나머지 HTML 태그 제거
      .replace(/\s+/g, ' ') // 연속 공백 정리
      .trim()
  }

  if (loading || loadingProduct) {
    return (
      <div className="flex items-center justify-center py-20 min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-on-surface-variant">로딩 중...</p>
        </div>
      </div>
    )
  }

  if (!shop || !product) {
    return null
  }

  return (
    <>
      <div className="space-y-6 max-w-5xl mx-auto">
          {/* 헤더: 뒤로가기 + 상품 정보 인라인 */}
          <div className="flex items-start gap-4">
            <Link
              to="/catalog"
              search={{ q: listSearch || undefined, page: listPage || undefined }}
              className="text-on-surface-variant hover:text-on-surface mt-1"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            {product.detail_image && (
              <img
                src={product.detail_image}
                alt={product.product_name}
                className="w-14 h-14 object-cover rounded border flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-on-surface truncate">
                  {product.product_name}
                </h1>
                <span className="text-sm text-outline-variant flex-shrink-0">#{product.product_no}</span>
                {product.has_override && (
                  <Badge variant="success" className="flex-shrink-0">커스텀 설정됨</Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 text-sm text-on-surface-variant">
                <span className="font-medium text-on-surface">
                  {priceRange && !priceRange.isSame
                    ? `${formatPrice(priceRange.min)} ~ ${formatPrice(priceRange.max)}`
                    : formatPrice(product.price)}
                </span>
                <span>·</span>
                <span>옵션 {product.variants?.length || 1}개</span>
              </div>
            </div>
          </div>

          {/* 동기화 진행 중 경고 */}
          {isSyncRunning && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-yellow-800">
                <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                <span className="text-sm font-medium">
                  동기화가 진행 중입니다. 완료될 때까지 편집이 제한됩니다.
                </span>
              </div>
            </div>
          )}

          {/* 카페24 원본 정보 (참조용, 접이식) */}
          <div className="border-b pb-1">
            <button
              type="button"
              onClick={() => setShowOriginalInfo(!showOriginalInfo)}
              className="flex items-center gap-2 text-sm text-on-surface-variant hover:text-on-surface transition-colors py-1"
            >
              <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showOriginalInfo ? `rotate-90` : ``}`} />
              카페24 원본 정보
            </button>
            {showOriginalInfo && (
              <div className="pt-3 pb-2 space-y-3">
                {/* 메인 이미지 + 설명 가로 배치 */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <span className="text-xs text-outline-variant block mb-1">메인 이미지</span>
                    {product.detail_image ? (
                      <img src={product.detail_image} alt="원본 이미지" className="w-20 h-20 object-cover rounded border" />
                    ) : (
                      <div className="w-20 h-20 bg-surface-low rounded border flex items-center justify-center">
                        <span className="text-xs text-outline-variant">없음</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-outline-variant block mb-1">설명</span>
                    {(() => {
                      const desc = stripHtml(product.description || product.summary_description || '').trim()
                      return desc ? (
                        <p className="text-sm text-on-surface-variant whitespace-pre-wrap line-clamp-4">{desc}</p>
                      ) : (
                        <p className="text-sm text-outline-variant">등록된 설명이 없어요</p>
                      )
                    })()}
                  </div>
                </div>
                {/* 추가 이미지 */}
                <div>
                  <span className="text-xs text-outline-variant block mb-1">추가 이미지</span>
                  {(product.additionalimages?.length ?? 0) > 0 ? (
                    <div className="flex gap-1.5">
                      {(product.additionalimages ?? []).slice(0, 8).map((img: Cafe24ProductResponseAdditionalimagesItem, i: number) => (
                        <img key={i} src={String(img.small || img.medium || img.big || '')} alt="" className="w-12 h-12 object-cover rounded border" />
                      ))}
                      {(product.additionalimages?.length ?? 0) > 8 && (
                        <span className="w-12 h-12 bg-surface-low rounded border flex items-center justify-center text-[10px] text-outline-variant">
                          +{(product.additionalimages?.length ?? 0) - 8}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-outline-variant">등록된 추가 이미지가 없어요</p>
                  )}
                </div>
              </div>
            )}
          </div>


          {/* 탭 네비게이션 (variant 2개 이상일 때만 표시) */}
          {(product.variants?.length ?? 0) > 1 && (
            <div className="flex border-b" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'product'}
                onClick={() => setActiveTab('product')}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'product'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-on-surface-variant hover:text-on-surface hover:border-border'
                }`}
              >
                상품 설정
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'variants'}
                onClick={() => setActiveTab('variants')}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'variants'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-on-surface-variant hover:text-on-surface hover:border-border'
                }`}
              >
                옵션별 설정
                <span className="ml-1.5 text-xs text-outline-variant">{(product.variants?.length ?? 0)}</span>
              </button>
            </div>
          )}

          {/* 상품 설정 탭 */}
          {activeTab === 'product' && (
          <Card>
            <CardContent className="space-y-6 pt-6">
              {/* ── 카탈로그 상품명 (REQ-174) ── */}
              <div>
                <label
                  htmlFor="catalog-custom-title"
                  className="text-sm font-medium text-on-surface block mb-2"
                >
                  카탈로그 상품명
                  <span className="text-outline-variant font-normal ml-1">
                    · 비우면 카페24 상품명이 적용돼요
                  </span>
                </label>
                <Input
                  id="catalog-custom-title"
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder={stripHtml(product.product_name || '')}
                  maxLength={CUSTOM_TITLE_MAX_LENGTH}
                  error={customTitleError}
                  className="bg-surface-lowest placeholder:text-border"
                />
                <p className="text-xs text-outline-variant mt-1">
                  쇼핑몰 상품명은 그대로 두고 메타 카탈로그에 보이는 이름만 바꿔요. 옵션별 상품명을 따로 정하면 그 옵션에는 옵션별 상품명이 쓰여요.
                </p>
              </div>

              {/* ── 설명 ── */}
              <div className="border-t pt-6">
                <label className="text-sm font-medium text-on-surface block mb-2">
                  설명
                  <span className="text-outline-variant font-normal ml-1">
                    {stripHtml(product.description || product.summary_description || '').trim()
                      ? '· 비우면 카페24 원본 설명이 적용돼요'
                      : '· 카페24에 등록된 설명이 없어요. 입력하지 않으면 상품명이 사용돼요'}
                  </span>
                </label>
                <Textarea
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  placeholder={
                    stripHtml(product.description || product.summary_description || '').trim()
                      ? stripHtml(product.description || product.summary_description || '').slice(0, 200)
                      : '메타 카탈로그에 표시할 설명을 입력하세요'
                  }
                  minRows={5}
                  className="bg-surface-lowest placeholder:text-border"
                />
              </div>

              {/* ── 미디어 ── */}
              <div className="border-t pt-6">
                <h4 className="text-base font-semibold text-on-surface">이미지 · 동영상</h4>
                {/* 업로드 제한을 미리 알려 실패 자체를 줄인다 (variant 탭과 같은 마크업) */}
                <p className="text-xs text-outline-variant mb-4">
                  {UPLOAD_LIMITS_HINT}
                  {isUploading && (
                    <span className="ml-2 inline-flex items-center gap-1 text-primary">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      업로드 중…
                    </span>
                  )}
                </p>

                {/* 메인 이미지 + 추가 이미지 가로 배치 */}
                <div className="flex gap-6">
                  {/* 메인 이미지 (고정 1개) */}
                  <div className="flex-shrink-0">
                    <label className="text-sm font-medium text-on-surface block mb-2">
                      메인 이미지
                      <span className="text-outline-variant font-normal ml-1">
                        {product.detail_image ? '· 비우면 카페24 이미지 적용' : '· 카페24 이미지 없음'}
                      </span>
                    </label>
                    <div className="w-28 h-28">
                      {customMainImageUrl ? (
                        <div className="relative group w-full h-full">
                          <img src={customMainImageUrl} alt="커스텀 메인 이미지"
                            className="w-full h-full object-cover rounded-lg border cursor-pointer"
                            onClick={() => setPreviewMedia({ type: 'image', url: customMainImageUrl })} />
                          <button type="button" onClick={() => setCustomMainImageUrl("")}
                            className="absolute top-1 right-1 p-1 bg-black/60 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : product.detail_image ? (
                        <label className="relative w-full h-full block cursor-pointer group">
                          <img src={product.detail_image} alt="카페24 이미지" className="w-full h-full object-cover rounded-lg border opacity-20" />
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            {isUploading ? <Loader2 className="h-5 w-5 text-outline-variant group-hover:text-primary transition-colors animate-spin" /> : <Plus className="h-5 w-5 text-outline-variant group-hover:text-primary transition-colors" />}
                            <span className="text-[10px] text-on-surface-variant">교체</span>
                          </div>
                          <span className="absolute bottom-1 left-1 text-[10px] text-outline-variant bg-surface-lowest/80 rounded px-1">카페24</span>
                          <input type="file" disabled={isUploading} accept={UPLOAD_LIMITS.image.accept} className="hidden" onChange={async (e) => {
                            const file = e.target.files?.[0]; if (!file) return
                            try {
                              const uploadData = await uploadFile(file, 'image', product.product_no)
                              setCustomMainImageUrl(uploadData.public_url)
                            } catch (error) { toast.error(`${file.name}: ${uploadErrorMessage(error)}`) }
                            e.target.value = ''
                          }} />
                        </label>
                      ) : (
                        <label className={`w-full h-full border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center transition-colors ${isUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary hover:bg-surface'}`}>
                          <input type="file" disabled={isUploading} accept={UPLOAD_LIMITS.image.accept} className="hidden" onChange={async (e) => {
                            const file = e.target.files?.[0]; if (!file) return
                            try {
                              const uploadData = await uploadFile(file, 'image', product.product_no)
                              setCustomMainImageUrl(uploadData.public_url)
                            } catch (error) { toast.error(`${file.name}: ${uploadErrorMessage(error)}`) }
                            e.target.value = ''
                          }} />
                          {isUploading ? <Loader2 className="h-5 w-5 text-outline-variant animate-spin" /> : <Plus className="h-5 w-5 text-outline-variant" />}
                          <span className="text-[10px] text-on-surface-variant mt-0.5">업로드</span>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* 세로 구분선 */}
                  <div className="w-px bg-border self-stretch" />

                  {/* 추가 이미지 (나머지 공간) */}
                  <div className="flex-1 min-w-0">
                    <label className="text-sm font-medium text-on-surface block mb-2">
                      추가 이미지 ({customImageUrls.length}/{CATALOG_MEDIA_MAX_COUNT.image}개)
                      <span className="text-outline-variant font-normal ml-1">
                        {(product.additionalimages?.length ?? 0) > 0
                          ? `· 비우면 카페24 이미지 ${(product.additionalimages?.length ?? 0)}개 적용`
                          : '· 카페24 추가 이미지 없음'}
                      </span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {customImageUrls.map((url, index) => (
                        <div key={index} className="relative group w-28 h-28">
                          <img src={url} alt={`커스텀 이미지 ${index + 1}`}
                            className="w-full h-full object-cover rounded-lg border cursor-pointer"
                            onClick={() => setPreviewMedia({ type: 'image', url })}
                            onError={(e) => { (e.target as HTMLImageElement).src = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><rect fill='%23f3f4f6' width='80' height='80'/><text fill='%239ca3af' font-size='12' x='20' y='44'>ERR</text></svg>" }} />
                          <button type="button" onClick={() => handleRemoveImageUrl(index)}
                            className="absolute top-1 right-1 p-1 bg-black/60 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      {customImageUrls.length < CATALOG_MEDIA_MAX_COUNT.image && (
                        <label className={`w-28 h-28 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center transition-colors ${isUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary hover:bg-surface'}`}>
                          <input type="file" disabled={isUploading} accept={UPLOAD_LIMITS.image.accept} multiple className="hidden" onChange={async (e) => {
                            const files = e.target.files; if (!files) return
                            const { accepted: filesToUpload, rejections } = prepareUploadBatch(
                              files, 'image', CATALOG_MEDIA_MAX_COUNT.image - customImageUrls.length,
                              `추가 이미지는 최대 ${CATALOG_MEDIA_MAX_COUNT.image}개라`,
                            )
                            toastUploadRejections(rejections)
                            for (const file of filesToUpload) {
                              try {
                                const uploadData = await uploadFile(file, 'image', product.product_no)
                                // 배치가 겹쳐도 상한을 넘기지 않도록 최신 상태 기준으로 확인
                                setCustomImageUrls((prev) => prev.length >= CATALOG_MEDIA_MAX_COUNT.image ? prev : [...prev, uploadData.public_url])
                              } catch (error) { toast.error(`${file.name}: ${uploadErrorMessage(error)}`) }
                            }
                            e.target.value = ''
                          }} />
                          {isUploading ? <Loader2 className="h-5 w-5 text-outline-variant animate-spin" /> : <Plus className="h-5 w-5 text-outline-variant" />}
                          <span className="text-[10px] text-on-surface-variant mt-0.5">추가</span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                {/* 동영상 */}
                <div className="mt-5">
                  <label className="text-sm font-medium text-on-surface block mb-2">
                    동영상 ({customVideoUrls.length}/{CATALOG_MEDIA_MAX_COUNT.video}개)
                  </label>
                  <div className="flex flex-wrap gap-2">
                  {customVideoUrls.map((url, index) => (
                    <div key={index} className="relative group w-32 h-20">
                      <video
                        src={url}
                        className="w-full h-full object-cover rounded-lg border cursor-pointer"
                        controls={false}
                        onClick={() => setPreviewMedia({ type: 'video', url })}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveVideoUrl(index)}
                        className="absolute top-1 right-1 p-1 bg-black/60 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {customVideoUrls.length < CATALOG_MEDIA_MAX_COUNT.video && (
                    <label className={`w-32 h-20 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center transition-colors ${isUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary hover:bg-surface'}`}>
                      <input
                        type="file"
                        disabled={isUploading}
                        accept={UPLOAD_LIMITS.video.accept}
                        multiple
                        className="hidden"
                        onChange={async (e) => {
                          const files = e.target.files
                          if (!files) return

                          const { accepted: filesToUpload, rejections } = prepareUploadBatch(
                            files,
                            'video',
                            CATALOG_MEDIA_MAX_COUNT.video - customVideoUrls.length,
                            `동영상은 최대 ${CATALOG_MEDIA_MAX_COUNT.video}개라`,
                          )
                          toastUploadRejections(rejections)

                          for (const file of filesToUpload) {
                            try {
                              const uploadData = await uploadFile(file, 'video', product.product_no)
                              // 배치가 겹쳐도 상한을 넘기지 않도록 최신 상태 기준으로 확인
                              setCustomVideoUrls((prev) =>
                                prev.length >= CATALOG_MEDIA_MAX_COUNT.video
                                  ? prev
                                  : [...prev, uploadData.public_url],
                              )
                            } catch (error) {
                              toast.error(`${file.name}: ${uploadErrorMessage(error)}`)
                            }
                          }
                          e.target.value = '' // input 초기화
                        }}
                      />
                      {isUploading ? <Loader2 className="h-6 w-6 text-outline-variant animate-spin" /> : <Plus className="h-6 w-6 text-outline-variant" />}
                      <span className="text-xs text-on-surface-variant mt-1">추가</span>
                    </label>
                  )}
                </div>

                  <p className="text-xs text-outline-variant mt-1.5">
                    동영상을 업로드하면 메타가 자동으로 다운로드하여 광고에 사용해요.
                  </p>
                </div>
              </div>

              {/* ── 브랜드 · 카테고리 ── */}
              <div className="border-t pt-6 space-y-6">
                <h4 className="text-base font-semibold text-on-surface">브랜드 · 카테고리</h4>

              {/* 브랜드명 */}
              <div>
                <label className="text-sm font-medium text-on-surface block mb-2">
                  브랜드명
                </label>
                <Input
                  type="text"
                  value={customBrand}
                  onChange={(e) => setCustomBrand(e.target.value)}
                  placeholder={
                    product?.default_brand_name
                      ? `기본값: ${product.default_brand_name}`
                      : '이 상품의 브랜드명'
                  }
                />
                <p className="text-xs text-on-surface-variant mt-1">
                  {product?.default_brand_name ? (
                    <>
                      입력하지 않으면 기본 브랜드명 "
                      <strong>{product.default_brand_name}</strong>"
                      {getSubjectParticle(product.default_brand_name)} 사용됩니다.
                    </>
                  ) : (
                    '개별 상품에 적용할 브랜드명입니다. 카탈로그 설정에서 기본 브랜드명을 설정할 수 있습니다.'
                  )}
                </p>
              </div>

              {/* 제품 카테고리 */}
              <div>
                <label className="text-sm font-medium text-on-surface block mb-2">
                  제품 카테고리
                  <span className="text-outline-variant font-normal ml-1">· 적합한 카테고리를 선택하면 광고 성과가 개선돼요</span>
                </label>
                {fbProductCategoryId ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-3 py-2 bg-primary/10 border border-primary/20 rounded-lg text-sm text-primary">
                      {(() => {
                        const cat = cachedFlatList?.find((c) => c.id === fbProductCategoryId)
                        return cat?.path || fbProductCategoryId
                      })()}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDraftCategoryId(fbProductCategoryId)
                        setShowCategoryModal(true)
                      }}
                    >
                      변경
                    </Button>
                    <button
                      type="button"
                      onClick={() => setFbProductCategoryId(null)}
                      className="p-1.5 text-outline-variant hover:text-on-surface-variant transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDraftCategoryId(fbProductCategoryId)
                      setShowCategoryModal(true)
                    }}
                  >
                    카테고리 선택
                  </Button>
                )}
                {/* 카테고리 매핑에서 자동 선택된 경우 표시 */}
                {product?.mapped_fb_product_category_id &&
                  fbProductCategoryId === product.mapped_fb_product_category_id &&
                  !product.override?.fb_product_category_id && (
                    <p className="text-xs text-primary mt-1">
                      카테고리 매핑 설정에서 자동 선택됨
                    </p>
                  )}
              </div>
              </div>

              {/* 상세 속성 (카테고리 세부 정보 + 옵션 연결) */}
              {((fbProductCategoryId && applicableFields.length > 0 && fieldDefs) ||
                (optionMappings && optionMappings.options.length > 0)) && (
                <div className="border-t pt-6">
                  <h4 className="text-base font-semibold text-on-surface mb-1">상세 속성</h4>
                  <p className="text-xs text-on-surface-variant mb-4">
                    입력하면 메타 광고 성과가 개선돼요. 선택사항이에요.
                  </p>

              {/* 카테고리별 세부 필드 */}
              {fbProductCategoryId &&
                applicableFields.length > 0 &&
                fieldDefs && (
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-on-surface mb-3">카테고리 속성</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {applicableFields.map((fieldKey) => {
                        const fieldDef = fieldDefs.field_definitions[fieldKey]
                        if (!fieldDef) return null

                        // 옵션 매핑으로 사용 중인 필드인지 확인
                        const isMappedFromOption = mappedMetaFields.has(fieldKey)

                        // capacity 필드의 현재 값 추출
                        const capacityValue = categorySpecificFields[fieldKey] as
                          | CapacityValue
                          | undefined
                        const stringValue =
                          typeof categorySpecificFields[fieldKey] === 'string'
                            ? (categorySpecificFields[fieldKey] as string)
                            : ''

                        return (
                          <div key={fieldKey}>
                            <label className="text-sm text-on-surface-variant block mb-1">
                              {fieldDef.label_ko}
                            </label>
                            {/* 옵션 매핑에서 사용 중인 필드는 비활성화 */}
                            {isMappedFromOption ? (
                              <div className="px-3 py-2 bg-surface-low border border-border rounded-md text-sm text-on-surface-variant">
                                아래 옵션 연결에서 자동 적용돼요
                              </div>
                            ) : fieldDef.type === 'capacity' ? (
                              /* capacity: 숫자 입력 + 단위 드롭다운 */
                              (<div className="flex gap-2">
                                <Input
                                  type="number"
                                  value={capacityValue?.value || ''}
                                  onChange={(e) =>
                                    setCategorySpecificFields((prev) => ({
                                      ...prev,
                                      [fieldKey]: {
                                        value: e.target.value,
                                        unit:
                                          (prev[fieldKey] as CapacityValue)
                                            ?.unit || 'ml',
                                      } as CapacityValue,
                                    }))
                                  }
                                  placeholder={fieldDef.placeholder_ko ?? undefined}
                                  className="flex-1"
                                  min={0}
                                />
                                <Select
                                  value={capacityValue?.unit || 'ml'}
                                  onChange={(e) =>
                                    setCategorySpecificFields((prev) => ({
                                      ...prev,
                                      [fieldKey]: {
                                        value:
                                          (prev[fieldKey] as CapacityValue)
                                            ?.value || '',
                                        unit: e.target.value,
                                      } as CapacityValue,
                                    }))
                                  }
                                  className="w-24"
                                >
                                  {fieldDef.units?.map((unit: CapacityUnit) => (
                                    <SelectOption key={unit.value} value={unit.value}>
                                      {unit.label_ko}
                                    </SelectOption>
                                  ))}
                                </Select>
                              </div>)
                            ) : fieldDef.type === 'number' ? (
                              /* number: 숫자 입력 (min/max 지원) */
                              (<Input
                                type="number"
                                value={stringValue}
                                onChange={(e) =>
                                  setCategorySpecificFields((prev) => ({
                                    ...prev,
                                    [fieldKey]: e.target.value,
                                  }))
                                }
                                placeholder={fieldDef.placeholder_ko ?? undefined}
                                min={fieldDef.min ?? undefined}
                                max={fieldDef.max ?? undefined}
                              />)
                            ) : fieldDef.type === 'string_with_suggestions' ? (
                              /* string_with_suggestions: 드롭다운 + 커스텀 입력 (datalist) */
                              (<>
                                <Input
                                  type="text"
                                  list={`${fieldKey}-suggestions`}
                                  value={stringValue}
                                  onChange={(e) =>
                                    setCategorySpecificFields((prev) => ({
                                      ...prev,
                                      [fieldKey]: e.target.value,
                                    }))
                                  }
                                  placeholder={fieldDef.placeholder_ko ?? undefined}
                                  maxLength={fieldDef.max_length ?? undefined}
                                />
                                <datalist id={`${fieldKey}-suggestions`}>
                                  {fieldDef.suggestions?.map((s: FieldOption) => (
                                    <option key={s.value} value={s.value}>
                                      {s.label_ko}
                                    </option>
                                  ))}
                                </datalist>
                              </>)
                            ) : fieldDef.type === 'enum' ? (
                              /* enum: 엄격한 선택 (드롭다운) */
                              ((() => {
                                // gender/age_group 자동 추출값 확인
                                const inferredValue =
                                  fieldKey === 'gender'
                                    ? inferredFields.gender
                                    : fieldKey === 'age_group'
                                      ? inferredFields.age_group
                                      : null
                                // 사용자가 명시적으로 설정했는지 확인 (빈 문자열 ""도 명시적 설정임)
                                const hasUserValue =
                                  fieldKey in categorySpecificFields
                                // 표시값: 사용자 설정 있으면 그 값, 없으면 자동 추출값
                                const displayValue = hasUserValue
                                  ? stringValue
                                  : inferredValue || ''
                                const isAutoInferred =
                                  !hasUserValue && !!inferredValue

                                return (
                                  <>
                                    <Select
                                      value={displayValue}
                                      onChange={(e) =>
                                        setCategorySpecificFields((prev) => ({
                                          ...prev,
                                          [fieldKey]: e.target.value,
                                        }))
                                      }
                                    >
                                      <SelectOption value="">선택 안함</SelectOption>
                                      {fieldDef.options?.map((opt: FieldOption) => (
                                        <SelectOption key={opt.value} value={opt.value}>
                                          {opt.label_ko}
                                        </SelectOption>
                                      ))}
                                    </Select>
                                    {isAutoInferred && (
                                      <p className="text-xs text-primary mt-1">
                                        💡 자동 추출
                                      </p>
                                    )}
                                  </>
                                )
                              })())
                            ) : (
                              /* string: 일반 텍스트 입력 */
                              (<Input
                                type="text"
                                value={stringValue}
                                onChange={(e) =>
                                  setCategorySpecificFields((prev) => ({
                                    ...prev,
                                    [fieldKey]: e.target.value,
                                  }))
                                }
                                placeholder={fieldDef.placeholder_ko ?? undefined}
                                maxLength={fieldDef.max_length ?? undefined}
                              />)
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

              {/* 옵션 연결 */}
              {optionMappings && optionMappings.options.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-on-surface mb-1">옵션 연결</h5>
                  <p className="text-xs text-on-surface-variant mb-3">
                    카페24의 옵션(색상, 사이즈 등)을 메타 속성에 연결하면, 각 옵션값이 자동으로 적용돼요.
                  </p>

                  {loadingOptionMappings ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-outline-variant" />
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      {/* 테이블 헤더 */}
                      <div className="flex items-center bg-surface-low border-b text-xs font-medium text-on-surface-variant">
                        <div className="w-40 px-3 py-2">카페24 옵션</div>
                        <div className="w-8 px-1 py-2 text-center">→</div>
                        <div className="flex-1 px-3 py-2">메타 카탈로그 속성</div>
                      </div>

                      {/* 매핑 행들 */}
                      {optionMappings.options.map((opt: OptionInfo) => {
                        const fieldCount = opt.mapping?.field
                          ? getFieldMappingCount(opt.mapping.field)
                          : 0
                        const showCombineOrder = fieldCount > 1
                        // 현재 옵션 제외하고 같은 필드에서 사용 중인 순번들
                        const usedOrders = opt.mapping?.field
                          ? getUsedCombineOrders(opt.mapping.field, opt.name)
                          : []

                        return (
                          <div
                            key={opt.name}
                            className="flex items-center border-b last:border-b-0 hover:bg-surface"
                          >
                            {/* 카페24 옵션명 */}
                            <div className="w-40 px-3 py-3 flex-shrink-0">
                              <div className="text-sm font-medium text-on-surface">
                                {stripHtml(opt.name)}
                              </div>
                              <div
                                className="text-xs text-outline-variant truncate"
                                title={opt.values.map(v => stripHtml(v)).join(', ')}
                              >
                                {opt.values.slice(0, 3).map(v => stripHtml(v)).join(', ')}
                                {opt.values.length > 3 && '...'}
                              </div>
                            </div>

                            {/* 화살표 */}
                            <div className="w-8 px-1 text-outline-variant text-center flex-shrink-0">
                              →
                            </div>

                            {/* 메타 속성 선택 */}
                            <div className="flex-1 px-3 py-2 flex items-center gap-2">
                              <Select
                                variant="sm"
                                value={opt.mapping?.field || ''}
                                onChange={(e) => {
                                  const newField = e.target.value || null
                                  const currentField = opt.mapping?.field
                                  // field가 변경되면 combine_order를 undefined로 전달해서 자동 할당
                                  handleOptionMappingChange(
                                    opt.name,
                                    newField,
                                    newField === currentField
                                      ? opt.mapping?.combine_order
                                      : undefined,
                                  )
                                }}
                                className="w-28"
                              >
                                <SelectOption value="">연결 안함</SelectOption>
                                {optionMappings.available_fields.map((field) => (
                                  <SelectOption key={field} value={field}>
                                    {META_FIELD_LABELS[field] || field}
                                  </SelectOption>
                                ))}
                              </Select>

                              {/* 결합 순서 (같은 필드에 2개 이상 매핑 시) */}
                              {showCombineOrder && opt.mapping?.field && (
                                <Select
                                  variant="sm"
                                  value={opt.mapping?.combine_order || 1}
                                  onChange={(e) =>
                                    handleOptionMappingChange(
                                      opt.name,
                                      opt.mapping?.field || null,
                                      Number(e.target.value),
                                    )
                                  }
                                  className="w-20"
                                >
                                  {[1, 2, 3, 4].map((order) => (
                                    <SelectOption
                                      key={order}
                                      value={order}
                                      disabled={usedOrders.includes(order)}
                                    >
                                      {order}번째
                                      {usedOrders.includes(order)
                                        ? ' (사용 중)'
                                        : ''}
                                    </SelectOption>
                                  ))}
                                </Select>
                              )}

                              {/* 자동 인식 표시 */}
                              {opt.is_auto_detected && (
                                <span className="text-xs text-primary">
                                  (자동)
                                </span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <p className="text-xs text-on-surface-variant mt-3">
                    같은 속성에 여러 옵션을 연결하면 "값1 / 값2" 형식으로 합쳐져요.
                  </p>
                </div>
              )}
              </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* 옵션별 설정 탭 (variant 2개 이상일 때만) */}
          {activeTab === 'variants' && (product.variants?.length ?? 0) > 1 && (
            <div className="space-y-4">
              {/* 안내 + 상태 요약 */}
              <p className="text-sm text-on-surface-variant">
                특정 옵션에 다른 타이틀이나 이미지를 사용하고 싶을 때 설정하세요.
              </p>
              <div className="flex items-center gap-4 text-sm text-on-surface-variant">
                <span>{(product.variants?.length ?? 0)}개 옵션</span>
                {(() => {
                  const notSelling = (product.variants ?? []).filter(v => v.selling !== 'T').length
                  return notSelling > 0 && <span className="text-amber-600">{notSelling}개 판매중지</span>
                })()}
              </div>

              {/* 테이블 */}
              <div className="border rounded-lg overflow-hidden bg-surface-lowest">
                {/* 테이블 — 모바일 가로 스크롤 */}
                <div className="overflow-x-auto">
                {/* 테이블 헤더 */}
                <div className="grid grid-cols-[1fr_110px_90px_80px_60px_36px] min-w-[540px] items-center border-b bg-surface px-3 sm:px-5 py-3 text-xs font-medium text-on-surface-variant">
                  <span>옵션</span>
                  <span className="text-right">가격</span>
                  <span className="text-center">상태</span>
                  <span className="text-center">개별 설정</span>
                  <span className="text-center">동기화 포함</span>
                  <span />
                </div>

                {/* Variant 행 */}
                {(() => {
                  const VARIANT_LIMIT = 20
                  const totalVariants = (product.variants?.length ?? 0)
                  const shouldCollapse = totalVariants > VARIANT_LIMIT
                  const displayedVariants =
                    shouldCollapse && !variantsExpanded
                      ? (product.variants ?? []).slice(0, VARIANT_LIMIT)
                      : (product.variants ?? [])

                  return (
                    <>
                      {displayedVariants.map((variant) => {
                        const vo = variantOverrides[variant.variant_code]
                        const isExcluded = vo?.exclude_from_catalog || false
                        const hasCustom = vo && (
                          vo.custom_title || vo.custom_description ||
                          vo.custom_main_image_url || vo.custom_image_urls?.length ||
                          vo.custom_video_urls?.length
                        )
                        const isExpanded = expandedVariantCode === variant.variant_code
                        const variantPrice = getVariantPrice(variant.additional_amount ?? 0)

                        // 판매 상태 판단
                        const isSelling = variant.selling === 'T'
                        const isDisplay = variant.display === 'T'

                        return (
                          <div key={variant.variant_code}>
                            {/* 메인 행 (클릭으로 확장/축소) */}
                            <div
                              onClick={() => setExpandedVariantCode(isExpanded ? null : variant.variant_code)}
                              className={`grid grid-cols-[1fr_110px_90px_80px_60px_36px] min-w-[540px] items-center px-3 sm:px-5 py-3.5 border-b last:border-b-0 text-sm transition-colors cursor-pointer ${
                                isExcluded
                                  ? 'bg-surface/80 opacity-60'
                                  : isExpanded
                                    ? 'bg-primary/5'
                                    : 'hover:bg-surface'
                              }`}
                            >
                              {/* 옵션명 */}
                              <div className="flex items-center gap-2 min-w-0">
                                {variant.image && (
                                  <img
                                    src={variant.image}
                                    alt=""
                                    className="w-8 h-8 rounded border object-cover flex-shrink-0"
                                  />
                                )}
                                <span className={`truncate ${isExcluded ? `text-on-surface-variant line-through` : `text-on-surface`}`} title={
                                  (variant.options ?? []).map((o) => stripHtml(String(o.value ?? ''))).join(' / ')
                                }>
                                  {(variant.options ?? []).map((o) => stripHtml(String(o.value ?? ''))).join(' / ') || variant.variant_code}
                                </span>
                              </div>

                              {/* 가격 */}
                              <span className="text-right font-medium tabular-nums text-on-surface">
                                {formatPrice(variantPrice)}
                              </span>

                              {/* 판매 상태 */}
                              <div className="flex justify-center">
                                {!isSelling ? (
                                  <Badge variant="muted" className="text-xs">판매중지</Badge>
                                ) : !isDisplay ? (
                                  <Badge variant="muted" className="text-xs">미진열</Badge>
                                ) : (
                                  <Badge variant="success" className="text-xs">판매중</Badge>
                                )}
                              </div>

                              {/* 개별 설정 여부 */}
                              <div className="flex justify-center">
                                {hasCustom ? (
                                  <Badge variant="info" className="text-xs">설정됨</Badge>
                                ) : (
                                  <span className="text-xs text-border">—</span>
                                )}
                              </div>

                              {/* 포함 토글 (상품 목록과 동일한 Toggle 스타일) */}
                              <div className="flex justify-center">
                                <button
                                  type="button"
                                  disabled={togglingVariant === variant.variant_code || isSyncRunning}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleVariantExclude(variant.variant_code, !isExcluded)
                                  }}
                                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 ${
                                    isExcluded ? 'bg-surface-high' : 'bg-green-500'
                                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                                  title={isExcluded ? '클릭하여 카탈로그에 포함' : '클릭하여 카탈로그에서 제외'}
                                >
                                  <span className={`inline-flex h-3.5 w-3.5 transform items-center justify-center rounded-full bg-surface-lowest shadow-sm transition-transform ${
                                    isExcluded ? 'translate-x-0.5' : 'translate-x-[18px]'
                                  }`}>
                                    {togglingVariant === variant.variant_code && (
                                      <Loader2 className="h-2.5 w-2.5 animate-spin text-outline-variant" />
                                    )}
                                  </span>
                                </button>
                              </div>

                              {/* 확장 인디케이터 */}
                              <div className="flex justify-center">
                                <ChevronRight className={`h-4 w-4 text-outline-variant transition-transform ${isExpanded ? `rotate-90` : ``}`} />
                              </div>
                            </div>

                            {/* 확장 패널: variant 커스텀 편집 */}
                            {isExpanded && (
                              <div className="border-b bg-surface-lowest px-8 py-5 space-y-4">
                                {/* 커스텀 타이틀 */}
                                <div>
                                  <label className="text-sm font-medium text-on-surface block mb-2">
                                    타이틀
                                    <span className="text-outline-variant font-normal ml-1">· 비우면 상품 설정의 타이틀이 적용돼요</span>
                                  </label>
                                  <Input
                                    type="text"
                                    value={vo?.custom_title || ''}
                                    onChange={(e) =>
                                      updateVariantOverride(variant.variant_code, 'custom_title', e.target.value || null)
                                    }
                                    placeholder={customTitle.trim() || stripHtml(product.product_name || '')}
                                    className="placeholder:text-border"
                                  />
                                </div>

                                {/* 커스텀 설명 */}
                                <div>
                                  <label className="text-sm font-medium text-on-surface block mb-2">
                                    설명
                                    <span className="text-outline-variant font-normal ml-1">· 비우면 상품 설정의 설명이 적용돼요</span>
                                  </label>
                                  <Textarea
                                    value={vo?.custom_description || ''}
                                    onChange={(e) =>
                                      updateVariantOverride(variant.variant_code, 'custom_description', e.target.value || null)
                                    }
                                    placeholder={
                                      customDescription
                                        ? customDescription.slice(0, 100) + (customDescription.length > 100 ? '...' : '')
                                        : '상품 설정의 설명이 적용돼요'
                                    }
                                    minRows={5}
                                    className="placeholder:text-border"
                                  />
                                </div>

                                {/* 업로드 제한 안내 (상품 설정 탭과 동일) */}
                                <p className="text-xs text-outline-variant">
                                  {UPLOAD_LIMITS_HINT}
                                  {isUploading && (
                                    <span className="ml-2 inline-flex items-center gap-1 text-primary">
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                      업로드 중…
                                    </span>
                                  )}
                                </p>

                                {/* 이미지: 메인 | 추가 가로 배치 (상품 설정과 동일 패턴) */}
                                <div className="flex gap-5">
                                  {/* 메인 이미지 */}
                                  <div className="flex-shrink-0">
                                    <label className="text-sm font-medium text-on-surface block mb-2">
                                      메인 이미지
                                      <span className="text-outline-variant font-normal ml-1">· 비우면 {variant.image ? "카페24 이미지" : "상품 설정"} 적용</span>
                                    </label>
                                    <div className="w-28 h-28">
                                      {vo?.custom_main_image_url ? (
                                        <div className="relative group w-full h-full">
                                          <img src={vo.custom_main_image_url} alt="커스텀" className="w-full h-full object-cover rounded-lg border cursor-pointer"
                                            onClick={() => setPreviewMedia({ type: 'image', url: vo.custom_main_image_url! })} />
                                          <button type="button" onClick={() => updateVariantOverride(variant.variant_code, "custom_main_image_url", null)}
                                            className="absolute -top-1 -right-1 p-0.5 bg-black/60 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                            <X className="h-3 w-3" />
                                          </button>
                                        </div>
                                      ) : (
                                        <label className={`w-full h-full border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center transition-colors ${isUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary hover:bg-surface'}`}>
                                          <input type="file" disabled={isUploading} accept={UPLOAD_LIMITS.image.accept} className="hidden" onChange={async (e) => {
                                            const file = e.target.files?.[0]; if (!file) return
                                            try {
                                              const uploadData = await uploadFile(file, 'image', product.product_no)
                                              updateVariantOverride(variant.variant_code, 'custom_main_image_url', uploadData.public_url)
                                            } catch (error) { toast.error(`${file.name}: ${uploadErrorMessage(error)}`) }
                                            e.target.value = ''
                                          }} />
                                          {isUploading ? <Loader2 className="h-4 w-4 text-outline-variant animate-spin" /> : <Plus className="h-4 w-4 text-outline-variant" />}
                                          <span className="text-[10px] text-on-surface-variant mt-0.5">업로드</span>
                                        </label>
                                      )}
                                    </div>
                                  </div>

                                  {/* 세로 구분선 */}
                                  <div className="w-px bg-border self-stretch" />

                                  {/* 추가 이미지 */}
                                  <div className="flex-1 min-w-0">
                                    <label className="text-sm font-medium text-on-surface block mb-2">
                                      추가 이미지 ({vo?.custom_image_urls?.length || 0}/{CATALOG_MEDIA_MAX_COUNT.image}개)
                                      <span className="text-outline-variant font-normal ml-1">· 비우면 상품 설정의 추가 이미지가 적용돼요</span>
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                      {(vo?.custom_image_urls || []).map((url, idx) => (
                                        <div key={idx} className="relative group w-28 h-28">
                                          <img src={url} alt="" className="w-full h-full object-cover rounded-lg border cursor-pointer"
                                            onClick={() => setPreviewMedia({ type: 'image', url })} />
                                          <button type="button" onClick={() => {
                                            const urls = [...(vo?.custom_image_urls || [])]; urls.splice(idx, 1)
                                            updateVariantOverride(variant.variant_code, 'custom_image_urls', urls.length > 0 ? urls : null)
                                          }} className="absolute -top-1 -right-1 p-0.5 bg-black/60 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                            <X className="h-3 w-3" />
                                          </button>
                                        </div>
                                      ))}
                                      {(vo?.custom_image_urls?.length || 0) < CATALOG_MEDIA_MAX_COUNT.image && (
                                        <label className={`w-28 h-28 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center transition-colors ${isUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary hover:bg-surface'}`}>
                                          <input type="file" disabled={isUploading} accept={UPLOAD_LIMITS.image.accept} multiple className="hidden" onChange={async (e) => {
                                            const files = e.target.files; if (!files) return
                                            const current = vo?.custom_image_urls || []
                                            const { accepted: filesToUpload, rejections } = prepareUploadBatch(
                                              files, 'image', CATALOG_MEDIA_MAX_COUNT.image - current.length,
                                              `추가 이미지는 최대 ${CATALOG_MEDIA_MAX_COUNT.image}개라`,
                                            )
                                            toastUploadRejections(rejections)
                                            for (const file of filesToUpload) {
                                              try {
                                                const uploadData = await uploadFile(file, 'image', product.product_no)
                                                appendVariantOverrideUrl(variant.variant_code, 'custom_image_urls', uploadData.public_url, CATALOG_MEDIA_MAX_COUNT.image)
                                              } catch (error) { toast.error(`${file.name}: ${uploadErrorMessage(error)}`) }
                                            }
                                            e.target.value = ''
                                          }} />
                                          {isUploading ? <Loader2 className="h-4 w-4 text-outline-variant animate-spin" /> : <Plus className="h-4 w-4 text-outline-variant" />}
                                          <span className="text-[10px] text-on-surface-variant mt-0.5">추가</span>
                                        </label>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* 동영상 */}
                                <div>
                                  <label className="text-sm font-medium text-on-surface block mb-2">
                                    동영상 ({vo?.custom_video_urls?.length || 0}/{CATALOG_MEDIA_MAX_COUNT.video}개)
                                    <span className="text-outline-variant font-normal ml-1">· 비우면 상품 설정의 동영상이 적용돼요</span>
                                  </label>
                                  <div className="flex flex-wrap gap-2">
                                    {(vo?.custom_video_urls || []).map((url, idx) => (
                                      <div key={idx} className="relative group">
                                        <video src={url} className="w-32 h-20 object-cover rounded-lg border cursor-pointer" controls={false}
                                          onClick={() => setPreviewMedia({ type: 'video', url })} />
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const urls = [...(vo?.custom_video_urls || [])]
                                            urls.splice(idx, 1)
                                            updateVariantOverride(variant.variant_code, 'custom_video_urls', urls.length > 0 ? urls : null)
                                          }}
                                          className="absolute -top-1 -right-1 p-0.5 bg-black/60 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </div>
                                    ))}
                                    {(vo?.custom_video_urls?.length || 0) < CATALOG_MEDIA_MAX_COUNT.video && (
                                      <label className={`w-32 h-20 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center transition-colors ${isUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary hover:bg-surface'}`}>
                                        <input
                                          type="file"
                                          disabled={isUploading}
                                          accept={UPLOAD_LIMITS.video.accept}
                                          multiple
                                          className="hidden"
                                          onChange={async (e) => {
                                            const files = e.target.files
                                            if (!files) return
                                            const current = vo?.custom_video_urls || []
                                            const { accepted: filesToUpload, rejections } = prepareUploadBatch(
                                              files, 'video', CATALOG_MEDIA_MAX_COUNT.video - current.length,
                                              `동영상은 최대 ${CATALOG_MEDIA_MAX_COUNT.video}개라`,
                                            )
                                            toastUploadRejections(rejections)
                                            for (const file of filesToUpload) {
                                              try {
                                                const uploadData = await uploadFile(file, 'video', product.product_no)
                                                appendVariantOverrideUrl(variant.variant_code, 'custom_video_urls', uploadData.public_url, CATALOG_MEDIA_MAX_COUNT.video)
                                              } catch (error) {
                                                toast.error(`${file.name}: ${uploadErrorMessage(error)}`)
                                              }
                                            }
                                            e.target.value = ''
                                          }}
                                        />
                                        {isUploading ? <Loader2 className="h-4 w-4 text-outline-variant animate-spin" /> : <Plus className="h-4 w-4 text-outline-variant" />}
                                      </label>
                                    )}
                                  </div>
                                </div>

                                {/* variant 개별 저장 */}
                                <div className="flex justify-end pt-2 border-t border-border/60">
                                  <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation()
                                      handleSaveVariant(variant.variant_code)
                                    }}
                                    // 업로드 중 저장하면 올리던 미디어가 빠진 채 전송된다
                                    disabled={savingVariant === variant.variant_code || isSyncRunning || isUploading}
                                    title={isUploading ? '업로드가 끝난 뒤 저장할 수 있어요' : undefined}
                                  >
                                    {savingVariant === variant.variant_code ? (
                                      <>
                                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                        저장 중...
                                      </>
                                    ) : isUploading ? (
                                      <>
                                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                        업로드 중...
                                      </>
                                    ) : (
                                      '이 옵션 저장'
                                    )}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}

                      {/* 더보기/접기 */}
                      {shouldCollapse && (
                        <div className="px-4 py-2 border-t">
                          <button
                            type="button"
                            onClick={() => setVariantsExpanded(!variantsExpanded)}
                            className="text-xs text-primary hover:text-primary"
                          >
                            {variantsExpanded
                              ? '접기'
                              : `+ ${totalVariants - VARIANT_LIMIT}개 더보기`}
                          </button>
                        </div>
                      )}
                    </>
                  )
                })()}
                </div>
              </div>
            </div>
          )}

          {/* 액션 버튼 (상품 설정 탭에서만 표시) */}
          {activeTab === 'product' && (
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              disabled={!product.has_override || saving || isSyncRunning}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              초기화 (카페24 원본으로)
            </Button>
            <div className="flex gap-3">
              <Link
                to="/catalog"
                search={{
                  q: listSearch || undefined,
                  page: listPage || undefined,
                }}
              >
                <Button variant="outline" disabled={saving || isUploading}>
                  취소
                </Button>
              </Link>
              {/* 업로드 중 저장하면 아직 URL 이 안 들어간 상태로 전송돼 올리던 미디어가 조용히 빠진다 */}
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || isSyncRunning || isUploading}
                title={isUploading ? '업로드가 끝난 뒤 저장할 수 있어요' : undefined}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    저장 중...
                  </>
                ) : isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    업로드 중...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    상품 설정 저장
                  </>
                )}
              </Button>
            </div>
          </div>
          )}
        </div>
      {/* 카테고리 선택 모달 */}
      {showCategoryModal && (
        <Dialog open={true} onOpenChange={() => setShowCategoryModal(false)} size="xl">
          <DialogContent>
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-on-surface">제품 카테고리 선택</h3>
              <p className="text-sm text-on-surface-variant">적합한 카테고리를 선택하면 메타 광고 성과가 개선돼요.</p>
              {/* draft에만 반영하고, 모달은 '선택 완료'로만 닫는다 (상위 카테고리 선택 후에도 하위로 계속 탐색 가능) */}
              <FbCategorySelector
                value={draftCategoryId}
                alwaysOpen
                onChange={setDraftCategoryId}
              />
              <div className="flex justify-end gap-3 pt-2 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCategoryModal(false)}
                >
                  취소
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={!draftCategoryId}
                  onClick={() => {
                    setFbProductCategoryId(draftCategoryId)
                    setShowCategoryModal(false)
                  }}
                >
                  선택 완료
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* 미디어 미리보기 모달 */}
      {previewMedia && (
        <Dialog open={true} onOpenChange={() => setPreviewMedia(null)} size="3xl">
          <DialogContent className="p-2 bg-black/95">
            {previewMedia.type === 'image' ? (
              <img
                src={previewMedia.url}
                alt="미리보기"
                className="max-w-full max-h-[80vh] mx-auto rounded"
              />
            ) : (
              <video
                src={previewMedia.url}
                className="max-w-full max-h-[80vh] mx-auto rounded"
                controls
                autoPlay
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog />
    </>
  )
}
